<template>
  <div>
    <div id="card-element" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-p-3"></div>
    <div id="card-errors" class="tw-text-red-500 tw-text-sm tw-mt-1" role="alert">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps<{
  stripeKey: string
  clientSecret: string
}>()

const emit = defineEmits<{
  complete: [value: boolean]
}>()

const error = ref('')
let stripe: any = null
let card: any = null

const style = {
  base: {
    color: '#6772e5',
    lineHeight: '18px',
    fontFamily: '"Open sans", "Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4',
    },
    fontWeight: 500,
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a',
  },
}

onMounted(() => {
  if (!(window as any).Stripe) {
    error.value = 'Stripe not loaded'
    return
  }

  stripe = (window as any).Stripe(props.stripeKey)
  const elements = stripe.elements()
  card = elements.create('card', { style })
  card.mount('#card-element')

  card.on('change', (event: any) => {
    error.value = event.error?.message ?? ''
    emit('complete', event.complete)
  })
})

onBeforeUnmount(() => {
  if (card) {
    card.destroy()
    card = null
  }
})

async function confirmPayment() {
  if (!card) {
    throw new Error('Card element not ready')
  }

  const result = await stripe.confirmCardPayment(props.clientSecret, {
    payment_method: { card },
  })

  if (result.error) {
    // The charge may already have succeeded even though confirmCardPayment
    // errored (e.g. a post-3DS finalize rejection racing the 3DS return).
    // Treating that as a failure strands the payment: the customer is charged
    // but the backend is never notified (JIRA-17615). The backend re-verifies
    // the intent against Stripe before recording, so this is safe.
    if (result.error.payment_intent?.status === 'succeeded') {
      return result.error.payment_intent
    }

    const retrieved = await stripe.retrievePaymentIntent(props.clientSecret)
    if (retrieved.paymentIntent?.status === 'succeeded') {
      return retrieved.paymentIntent
    }

    error.value = result.error.message
    throw result.error
  }

  return result.paymentIntent
}

function setError(message: string) {
  error.value = message
}

defineExpose({ confirmPayment, setError })
</script>

